import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { brand, neutral, statusTone } from '../../theme';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import { formatDuration, formatTimecode } from '../../utils/timecode';
import StatusChip from '../ui/StatusChip';
import EmptyState from '../ui/EmptyState';
import PriorityFlag from '../Projects/PriorityFlag';
import type { StudioAnnotation } from '../AnnotationStudio/annotationModel';
import {
  TASK_STATUS_CHOICES,
  TaskStatusChoice,
  WorkspaceTask,
  formatMinutes,
  summarizeTasks,
  taskEnd,
  taskStart,
  taskStatusKey,
  taskStatusMeta,
  taskTypeLabel,
} from './taskMeta';

const TC_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, monospace';

const StatusDot: React.FC<{ color: string; size?: number }> = ({ color, size = 8 }) => (
  <Box component="span" aria-hidden="true" sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0, display: 'inline-block' }} />
);

interface TaskStatusMenuProps {
  task: WorkspaceTask;
  updating: boolean;
  onChange: (taskId: string, status: TaskStatusChoice) => void;
}

/** The task's status as a button; the menu moves it between To do, In progress and Done. */
export const TaskStatusMenu: React.FC<TaskStatusMenuProps> = ({ task, updating, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const key = taskStatusKey(task.status);
  const current = taskStatusMeta(task.status);
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={`${current.label}, change status of ${task.title}`}
        disabled={updating}
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={updating ? <CircularProgress size={12} color="inherit" /> : <StatusDot color={current.color} />}
        endIcon={<ExpandMoreOutlinedIcon />}
        sx={{
          borderColor: brand.borderStrong,
          color: 'text.primary',
          fontWeight: 600,
          py: 0.25,
          '& .MuiButton-startIcon': { ml: 0 },
          '& .MuiButton-endIcon': { ml: 0.25 },
        }}
      >
        {current.label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {TASK_STATUS_CHOICES.map((choice) => {
          const meta = taskStatusMeta(choice);
          const selected = key === choice;
          return (
            <MenuItem
              key={choice}
              selected={selected}
              onClick={() => {
                setAnchor(null);
                if (!selected) onChange(task.id, choice);
              }}
            >
              <ListItemIcon><StatusDot color={meta.color} size={10} /></ListItemIcon>
              <ListItemText>{meta.label}</ListItemText>
              {selected && <CheckOutlinedIcon fontSize="small" sx={{ ml: 2, color: 'text.secondary' }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

interface TaskCardProps {
  task: WorkspaceTask;
  linked: StudioAnnotation[];
  fps: number;
  editable: boolean;
  updating: boolean;
  onStatusChange: (taskId: string, status: TaskStatusChoice) => void;
  onEdit: (task: WorkspaceTask) => void;
  onDelete: (taskId: string) => void;
  onJumpTo: (seconds: number) => void;
}

export const TaskCard = memo(function TaskCard({
  task,
  linked,
  fps,
  editable,
  updating,
  onStatusChange,
  onEdit,
  onDelete,
  onJumpTo,
}: TaskCardProps) {
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const status = taskStatusMeta(task.status);
  const done = taskStatusKey(task.status) === 'COMPLETED';
  const start = taskStart(task);
  const end = taskEnd(task);
  const linkedTexts = new Set(linked.map((annotation) => annotation.text.trim()));
  const showDescription = Boolean(task.description && !linkedTexts.has(task.description.trim()));
  const timing = [
    task.estimatedMinutes ? `Est. ${formatMinutes(task.estimatedMinutes)}` : null,
    task.actualMinutes ? `actual ${formatMinutes(task.actualMinutes)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Paper
      variant="outlined"
      data-task-id={task.id}
      data-testid={`task-card-${task.id}`}
      sx={{
        position: 'relative',
        borderRadius: 3,
        borderColor: brand.border,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: status.color },
      }}
    >
      <Box sx={{ pl: 2.5, pr: 1, pt: 1.75, pb: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{
                fontSize: 15.5,
                fontWeight: 700,
                lineHeight: 1.35,
                color: done ? 'text.secondary' : 'text.primary',
                textDecoration: done ? 'line-through' : 'none',
                overflowWrap: 'anywhere',
              }}
            >
              {task.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.25, rowGap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
              {editable ? (
                <TaskStatusMenu task={task} updating={updating} onChange={onStatusChange} />
              ) : (
                <StatusChip kind="task" status={taskStatusKey(task.status)} />
              )}
              {start !== null && (
                <Tooltip title="Play this moment">
                  <Button
                    size="small"
                    onClick={() => onJumpTo(start)}
                    startIcon={<OndemandVideoOutlinedIcon sx={{ fontSize: 16 }} />}
                    aria-label={`Play task from ${formatTimecode(start, fps)}`}
                    sx={{ py: 0, px: 0.75, minWidth: 0, fontFamily: TC_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 12, fontWeight: 600, color: 'text.primary' }}
                  >
                    {formatTimecode(start, fps)}
                    {end !== null && ` → ${formatTimecode(end, fps)}`}
                  </Button>
                </Tooltip>
              )}
              {end !== null && start !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(end - start)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">{taskTypeLabel(task.type)}</Typography>
              <PriorityFlag priority={task.priority} />
              {timing && (
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {timing}
                </Typography>
              )}
            </Box>
          </Box>
          {editable && (
            <>
              <IconButton
                size="small"
                aria-label={`More actions for ${task.title}`}
                aria-haspopup="menu"
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <MoreVertOutlinedIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { setMenuAnchor(null); onEdit(task); }}>
                  <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Edit task</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); onDelete(task.id); }} sx={{ color: 'error.main' }}>
                  <ListItemIcon sx={{ color: 'inherit' }}><DeleteOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Delete task</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {linked.length > 0 && (
          <Box sx={{ mt: 1.25, mr: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {linked.map((annotation) => {
              const category = getAnnotationCategory(annotation.category, annotation.color);
              const { Icon } = category;
              return (
                <Box
                  key={annotation.id}
                  component="blockquote"
                  sx={{
                    m: 0,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: neutral[50],
                    borderLeft: `3px solid ${category.color}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, flexWrap: 'wrap' }}>
                    <Box component="span" sx={{ display: 'inline-flex', color: category.color, '& svg': { fontSize: 14 } }}>
                      <Icon />
                    </Box>
                    <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary' }}>
                      Creator feedback · {category.label}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11.5, fontFamily: TC_FONT, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatTimecode(annotation.time, fps)}
                      {annotation.endTime && annotation.endTime > annotation.time ? ` → ${formatTimecode(annotation.endTime, fps)}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    “{annotation.text}”
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {showDescription && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, mr: 1.5, whiteSpace: 'pre-wrap' }}>
            {task.description}
          </Typography>
        )}

        {task.aiInstructions && (
          <Box sx={{ mt: 1.25, mr: 1.5, borderRadius: 2, border: `1px solid ${brand.border}`, bgcolor: neutral[50] }}>
            <Box
              component="button"
              type="button"
              onClick={() => setGuidanceOpen((open) => !open)}
              aria-expanded={guidanceOpen}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                color: 'text.primary',
                '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: -2, borderRadius: '8px' },
              }}
            >
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Suggested approach
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 12, color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', visibility: guidanceOpen ? 'hidden' : 'visible' }}
              >
                {task.aiInstructions}
              </Typography>
              <ExpandMoreOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', transform: guidanceOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
            </Box>
            <Collapse in={guidanceOpen} unmountOnExit>
              <Typography variant="body2" sx={{ px: 1.5, pb: 1.25, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {task.aiInstructions}
              </Typography>
            </Collapse>
          </Box>
        )}

        {task.reviewComments && (
          <Alert severity="warning" icon={false} sx={{ mt: 1.25, mr: 1.5, py: 0.25 }}>
            <Typography variant="body2"><strong>Creator review:</strong> {task.reviewComments}</Typography>
          </Alert>
        )}

        {task.editorNotes && (
          <Typography variant="body2" sx={{ mt: 1.25, mr: 1.5, color: 'text.secondary', overflowWrap: 'anywhere' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Your notes: </Box>
            {task.editorNotes}
          </Typography>
        )}

        {(task.assetRefs?.length ?? 0) > 0 && (
          <Box sx={{ mt: 1.25, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Assets</Typography>
            {(task.assetRefs || []).map((ref) => (
              <Chip key={ref} size="small" label={ref} variant="outlined" sx={{ height: 22 }} />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
});

interface TaskBoardProps {
  tasks: WorkspaceTask[];
  annotationsById: Map<string, StudioAnnotation>;
  fps: number;
  editable: boolean;
  finished: boolean;
  autoGenerating: boolean;
  taskUpdating: Record<string, boolean>;
  /** Generate / suggestions / export actions (TaskManagementPanel toolbar). */
  actions: React.ReactNode;
  onCreate: () => void;
  onStatusChange: (taskId: string, status: TaskStatusChoice) => void;
  onEdit: (task: WorkspaceTask) => void;
  onDelete: (taskId: string) => void;
  onJumpTo: (seconds: number) => void;
}

const STATUS_GROUPS = ['IN_PROGRESS', 'REVIEW_NEEDED', 'BLOCKED', 'PENDING', 'COMPLETED'];

const SummaryStat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ minWidth: 88 }}>
    <Typography variant="caption" component="p" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 750, color: color || 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>{value}</Typography>
  </Box>
);

/**
 * The Tasks tab: a progress summary, generate/suggestions/export actions,
 * and the task cards grouped by status or in timeline order.
 */
const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  annotationsById,
  fps,
  editable,
  finished,
  autoGenerating,
  taskUpdating,
  actions,
  onCreate,
  onStatusChange,
  onEdit,
  onDelete,
  onJumpTo,
}) => {
  const [grouping, setGrouping] = useState<'status' | 'timeline'>('status');
  const [search, setSearch] = useState('');
  const summary = useMemo(() => summarizeTasks(tasks), [tasks]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return tasks;
    return tasks.filter((task) =>
      [task.title, task.description, task.aiInstructions, task.editorNotes]
        .some((field) => String(field || '').toLowerCase().includes(needle))
    );
  }, [tasks, search]);

  const byTime = useMemo(
    () => [...filtered].sort((a, b) => (taskStart(a) ?? Number.MAX_VALUE) - (taskStart(b) ?? Number.MAX_VALUE)),
    [filtered]
  );

  const groups = useMemo(() => {
    if (grouping === 'timeline') return [{ key: 'all', label: 'In timeline order', tasks: byTime }];
    const map = new Map<string, WorkspaceTask[]>();
    for (const task of byTime) {
      const key = taskStatusKey(task.status);
      const bucket = STATUS_GROUPS.includes(key) ? key : 'PENDING';
      map.set(bucket, [...(map.get(bucket) || []), task]);
    }
    return STATUS_GROUPS
      .filter((key) => map.has(key))
      .map((key) => ({ key, label: taskStatusMeta(key).label, tasks: map.get(key) || [] }));
  }, [grouping, byTime]);

  const linkedFor = (task: WorkspaceTask) =>
    (task.generatedFromAnnotations || [])
      .map((id) => annotationsById.get(id))
      .filter((annotation): annotation is StudioAnnotation => Boolean(annotation));

  const overEstimate = summary.actualMinutes > 0 && summary.estimatedMinutes > 0 && summary.actualMinutes > summary.estimatedMinutes;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: brand.border, p: { xs: 2, md: 2.5 } }} data-testid="task-summary">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 240px', minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{summary.percent}%</Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.completed} of {summary.total} task{summary.total === 1 ? '' : 's'} done
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={summary.percent}
              aria-label={`${summary.completed} of ${summary.total} tasks done`}
              sx={{ height: 8, borderRadius: 4, bgcolor: neutral[200], '& .MuiLinearProgress-bar': { bgcolor: statusTone.done.main } }}
            />
          </Box>
          <SummaryStat label="In progress" value={summary.inProgress} />
          <SummaryStat label="To do" value={summary.pending} />
          <SummaryStat label="Estimated" value={formatMinutes(summary.estimatedMinutes)} />
          <SummaryStat label="Actual" value={summary.actualMinutes > 0 ? formatMinutes(summary.actualMinutes) : '—'} color={overEstimate ? statusTone.problem.fg : undefined} />
          <SummaryStat label="Remaining" value={formatMinutes(summary.remainingMinutes)} />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grouping}
          onChange={(_, value) => value && setGrouping(value)}
          aria-label="Group tasks"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5 } }}
        >
          <ToggleButton value="status">By status</ToggleButton>
          <ToggleButton value="timeline">Timeline order</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search tasks"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          inputProps={{ 'aria-label': 'Search tasks' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ width: { xs: '100%', sm: 240 } }}
        />
        <Box sx={{ flex: 1 }} />
        {actions}
        {editable && (
          <Button variant="contained" size="small" startIcon={<AddTaskOutlinedIcon />} onClick={onCreate} disabled={finished}>
            Create task
          </Button>
        )}
      </Box>

      {autoGenerating && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: brand.border }} role="status">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Generating tasks from the creator&apos;s feedback…</Typography>
          <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
        </Paper>
      )}

      {tasks.length === 0 && !autoGenerating ? (
        <Card>
          <EmptyState
            compact
            icon={<AssignmentOutlinedIcon />}
            title="No tasks yet"
            description={
              finished
                ? 'No tasks were created for this project.'
                : 'Tasks are generated from the creator’s time-coded feedback. Generate them above, or create one yourself.'
            }
          />
        </Card>
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No tasks match “{search}”.</Typography>
      ) : (
        groups.map((group) => (
          <Box key={group.key} component="section" aria-label={group.label}>
            {grouping === 'status' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StatusDot color={taskStatusMeta(group.key).color} />
                <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {group.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{group.tasks.length}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {group.tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  linked={linkedFor(task)}
                  fps={fps}
                  editable={editable && !finished}
                  updating={Boolean(taskUpdating[task.id])}
                  onStatusChange={onStatusChange}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onJumpTo={onJumpTo}
                />
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default TaskBoard;
